import { ReactNode } from 'react';
import { Plural, Trans } from '@lingui/react/macro';

import ContactDetails from '@/components/ContactDetails';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import IconExternalLink from '@/icons/ExternalLink.svg';
import { BrandRequestMainContent } from '@/layouts/Brand/BrandRequestLayout/BrandRequestLayout';
import { isCancelled, RequestWithRelations } from '@/models/request';
import { SHIPMENT_CARRIERS, useShipments } from '@/models/shipment';
import Refused from '@/routes/Brand/Requests/Request/components/ClientRequestView/components/Refused';
import { ClientArticlesList } from '@/routes/Brand/Requests/Request/components/shared/ClientArticlesList';
import ClientStepper from '@/routes/Brand/Requests/Request/components/shared/ClientStepper';
import { PickupPointInfo } from '@/routes/Brand/Requests/Request/components/shared/PickupPointInfo/PickupPointInfo';

import { StepMessage } from '../../shared/StepMessage';

const Delivery = ({ request }: { request: RequestWithRelations }) => {
  let messageContent: ReactNode | null = null;

  const articlesInTransitStep = request.articles.filter(
    (article) => article.step?.step === 'transit'
  );

  const areAllArticlesPendingClientPickup = articlesInTransitStep.every(
    (article) =>
      article.step?.step === 'transit' &&
      !!request.storeId &&
      article.atEntityId === request.storeId &&
      article.toClient &&
      article.step.config.clientPickup
  );

  const articlesNotInPendingClientPickup = articlesInTransitStep.filter(
    (article) =>
      article.step?.step === 'transit' &&
      !(
        !!request.storeId &&
        article.atEntityId === request.storeId &&
        article.toClient &&
        article.step.config.clientPickup
      )
  );

  const areAllArticlesInTransit = articlesNotInPendingClientPickup.every(
    (article) => article.step?.step === 'transit' && article.inTransit
  );

  const { data: { shipments } = { shipments: [] } } = useShipments(
    {
      requestId: request.id,
    },
    {
      enabled: areAllArticlesInTransit,
    }
  );

  const shipmentBackToClient = shipments.find(
    (shipment) => shipment.status === 'in-transit' && !!shipment.destinationClientId
  );

  const shipmentBackToClientCarrier = SHIPMENT_CARRIERS.find(
    (carrier) => carrier.id === shipmentBackToClient?.carrier
  );

  if (areAllArticlesPendingClientPickup && isCancelled(request)) {
    return <Refused request={request} articles={articlesInTransitStep} />;
  }

  if (areAllArticlesPendingClientPickup) {
    messageContent = (
      <>
        <StepMessage
          message={
            <Trans id="client.request.delivery.pending-client">
              Your <Plural value={articlesInTransitStep.length} one="item is" other="items are" />{' '}
              at the <b>{request.store?.name} store</b>. You can come to pick{' '}
              <Plural value={articlesInTransitStep.length} one="it" other="them" /> up at any time
              during the store's opening hours.
            </Trans>
          }
        />

        <Box>
          <ContactDetails
            type="store"
            name={request.store?.name}
            email={request.store?.address?.contactEmail}
            address={request.store?.address}
            phone={request.store?.contactInfo?.phone}
            openingHours={request.store?.storeConfig?.openingHours}
          />
        </Box>
      </>
    );
  } else if (!areAllArticlesInTransit) {
    const message =
      request.client?.deliveryOption === 'store' ? (
        <Trans id="client.request.delivery.pending.store">
          The workshop is preparing your{' '}
          <Plural value={articlesNotInPendingClientPickup.length} one="item" other="items" /> for
          its shipment back to the store.
        </Trans>
      ) : (
        <Trans id="client.request.delivery.pending.client">
          The workshop is preparing your{' '}
          <Plural value={articlesNotInPendingClientPickup.length} one="item" other="items" /> for
          its shipment back to you.
        </Trans>
      );

    messageContent = <StepMessage message={message} />;
  } else {
    const message =
      request.client?.deliveryOption === 'store' ? (
        <Trans id="client.request.delivery.in-transit.store">
          Your{' '}
          <Plural
            value={articlesNotInPendingClientPickup.length}
            one="item is on its"
            other="items are on their"
          />{' '}
          way back to the store.
        </Trans>
      ) : (
        <>
          {!!shipmentBackToClientCarrier && (
            <>
              <Trans id="client.request.delivery.in-transit.client.carrier">
                {shipmentBackToClientCarrier?.name} has taken custody of your package.
              </Trans>{' '}
            </>
          )}
          <Trans id="client.request.delivery.in-transit.client">
            Your{' '}
            <Plural
              value={articlesNotInPendingClientPickup.length}
              one="item is on its"
              other="items are on their"
            />{' '}
            way back to you.
          </Trans>
        </>
      );

    messageContent = (
      <Stack gap="0.25rem">
        <StepMessage message={message} />

        {shipmentBackToClient?.trackingUrl && shipmentBackToClient?.trackingId && (
          <Stack row gap="0.25rem" alignItems="center">
            <Button
              variant="link"
              href={shipmentBackToClient.trackingUrl}
              target="_blank"
              size="large"
            >
              <Trans id="client.request.delivery.in-transit.client.tracking">
                Track your delivery ({shipmentBackToClient.trackingId})
              </Trans>
            </Button>
            <IconExternalLink style={{ fontSize: '1.25rem' }} />
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <BrandRequestMainContent>
      <Stack gap="1.5rem">
        <ClientStepper request={request} />
        {messageContent}
        <Stack gap="1rem">
          <PickupPointInfo request={request} />
          <ClientArticlesList request={request} showPrice showArticleComment />
        </Stack>
      </Stack>
    </BrandRequestMainContent>
  );
};

export default Delivery;
